import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, PlusIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { Button } from '../../components/ui/Button';
import { addressAPI } from '../../api/api';
import { salesApiService } from '../../api/salesApi';
import CreatableSelect from 'react-select/creatable';
import { getSalesCallStatusLabel } from '../../utils/salesCallStatusMap';

const SubAgencyFormModal = ({ isOpen, onClose, onSave, editingItem, addresses = [], onOpenSalesCall, refreshSalesCallsTrigger = 0 }) => {
    const [formData, setFormData] = useState({
        address: '',
        name: '',
        last_name: '',
        phone: '',
        email: '',
        company_name: '',
        company_address: '',
        company_country: '',
        company_city: '',
        company_country_id: '',
        company_city_id: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [isCountryModalOpen, setIsCountryModalOpen] = useState(false);
    const [isCityModalOpen, setIsCityModalOpen] = useState(false);
    const [newCountryName, setNewCountryName] = useState('');
    const [newCityName, setNewCityName] = useState('');
    const [isCreatingCountry, setIsCreatingCountry] = useState(false);
    const [isCreatingCity, setIsCreatingCity] = useState(false);
    const [salesCalls, setSalesCalls] = useState([]);
    const [loadingSalesCalls, setLoadingSalesCalls] = useState(false);

    useEffect(() => {
        if (editingItem) {
            setFormData({
                address: editingItem.address || '',
                name: editingItem.name || '',
                last_name: editingItem.last_name || '',
                phone: editingItem.phone || '',
                email: editingItem.email || '',
                company_name: editingItem.company_name || '',
                company_address: editingItem.company_address || '',
                company_country: editingItem.company_country || '',
                company_city: editingItem.company_city || '',
                company_country_id: '',
                company_city_id: '',
            });
        } else {
            setFormData({
                address: '',
                name: '',
                last_name: '',
                phone: '',
                email: '',
                company_name: '',
                company_address: '',
                company_country: '',
                company_city: '',
                company_country_id: '',
                company_city_id: '',
            });
        }
    }, [editingItem, isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const fetchCountries = async () => {
            try {
                const response = await salesApiService.getMarketingCountries();
                const list = response?.data || response?.results || response || [];
                setCountries(Array.isArray(list) ? list : []);
            } catch (error) {
                console.error('Country fetch error:', error);
                setCountries([]);
            }
        };
        fetchCountries();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const fetchAllCities = async () => {
            try {
                const response = await salesApiService.getMarketingCities();
                const list = response?.data || response?.results || response || [];
                setCities(Array.isArray(list) ? list : []);
            } catch (error) {
                console.error('City fetch error:', error);
                setCities([]);
            }
        };
        fetchAllCities();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !editingItem) {
            setSalesCalls([]);
            return;
        }
        const agencyName = editingItem.company_name || `${editingItem.name || ''} ${editingItem.last_name || ''}`.trim();
        if (!agencyName) {
            setSalesCalls([]);
            return;
        }
        const fetchSalesCalls = async () => {
            setLoadingSalesCalls(true);
            try {
                const response = await salesApiService.getSalesCalls({ agency: agencyName, page: 1 });
                const list = response?.data ?? response?.results ?? (Array.isArray(response) ? response : []);
                setSalesCalls(Array.isArray(list) ? list : []);
            } catch (error) {
                console.error('Sales calls fetch error:', error);
                setSalesCalls([]);
            } finally {
                setLoadingSalesCalls(false);
            }
        };
        fetchSalesCalls();
    }, [isOpen, editingItem, refreshSalesCallsTrigger]);

    const filteredCities = useMemo(() => {
        if (!formData.company_country_id) return cities;

        const selectedCountry = countries.find(
            (country) => String(country.id) === String(formData.company_country_id)
        );
        const selectedCountryName = (selectedCountry?.country_name || '').toLowerCase();

        return cities.filter((city) => {
            // Birincil: FK id eşleşmesi
            if (city.country && String(city.country) === String(formData.company_country_id)) {
                return true;
            }
            // Fallback: API country_name üzerinden eşleşme
            if (selectedCountryName && city.country_name) {
                return city.country_name.toLowerCase() === selectedCountryName;
            }
            return false;
        });
    }, [cities, countries, formData.company_country_id]);

    const countryOptions = useMemo(
        () =>
            countries.map((country) => ({
                value: String(country.id),
                label: country.country_name,
            })),
        [countries]
    );

    const cityOptions = useMemo(
        () =>
            filteredCities.map((city) => ({
                value: String(city.id),
                label: city.city_name,
            })),
        [filteredCities]
    );

    const openCountryModal = () => {
        setNewCountryName('');
        setIsCountryModalOpen(true);
    };

    const openCityModal = () => {
        if (!formData.company_country_id) {
            alert('Önce ülke seçiniz.');
            return;
        }
        setNewCityName('');
        setIsCityModalOpen(true);
    };

    const handleCreateCountry = async (e) => {
        e.preventDefault();
        const countryName = newCountryName.trim();
        if (!countryName) return;

        setIsCreatingCountry(true);
        try {
            const response = await salesApiService.createMarketingCountry({
                country_name: countryName,
                market: null,
            });

            if (!response?.id) {
                alert('Ülke oluşturulamadı.');
                return;
            }

            const refreshed = await salesApiService.getMarketingCountries();
            const list = refreshed?.data || refreshed?.results || refreshed || [];
            const normalized = Array.isArray(list) ? list : [];
            setCountries(normalized);

            const createdId = String(response.id);
            setFormData((prev) => ({
                ...prev,
                company_country_id: createdId,
                company_country: response.country_name || countryName,
                company_city_id: '',
                company_city: '',
            }));
            setIsCountryModalOpen(false);
        } catch (error) {
            console.error('Create country error:', error);
            alert('Ülke oluşturulamadı.');
        } finally {
            setIsCreatingCountry(false);
        }
    };

    const handleCreateCity = async (e) => {
        e.preventDefault();
        const cityName = newCityName.trim();
        if (!cityName || !formData.company_country_id) return;

        setIsCreatingCity(true);
        try {
            const response = await salesApiService.createMarketingCity({
                city_name: cityName,
                country: parseInt(formData.company_country_id, 10),
            });

            if (!response?.id) {
                alert('Şehir oluşturulamadı.');
                return;
            }

            const refreshed = await salesApiService.getMarketingCities();
            const list = refreshed?.data || refreshed?.results || refreshed || [];
            const normalized = Array.isArray(list) ? list : [];
            setCities(normalized);

            setFormData((prev) => ({
                ...prev,
                company_city_id: String(response.id),
                company_city: response.city_name || cityName,
            }));
            setIsCityModalOpen(false);
        } catch (error) {
            console.error('Create city error:', error);
            alert('Şehir oluşturulamadı.');
        } finally {
            setIsCreatingCity(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const payload = {
                ...formData,
                // Backend SubAgency serializer sadece text alanları bekliyor
                company_country_id: undefined,
                company_city_id: undefined,
            };

            // Address seçilmediyse ve firma adres bilgileri doluysa otomatik Address oluştur
            if (!payload.address) {
                // İş akışını bloklamamak için alanlar boş olsa da minimal Address oluştur.
                const addressResponse = await addressAPI.createAddress({
                    name: payload.name?.trim() || payload.company_name?.trim() || 'Yeni Adres',
                    lname: payload.last_name?.trim() || '',
                    phone: payload.phone?.trim() || '',
                    email: payload.email?.trim() || '',
                    address: payload.company_address?.trim() || '',
                    country: payload.company_country?.trim() || '',
                    city: payload.company_city?.trim() || '',
                    country_ref: payload.company_country_id ? parseInt(payload.company_country_id, 10) : null,
                    city_ref: payload.company_city_id ? parseInt(payload.company_city_id, 10) : null,
                });

                if (!addressResponse?.success || !addressResponse?.id) {
                    alert('Address kaydı otomatik oluşturulamadı. Lütfen tekrar deneyin.');
                    return;
                }

                payload.address = addressResponse.id;
            }

            // Sub-agency kartı için zorunlu mapleme:
            // - Alt acente adı (company_name) boşsa kişi adından üret
            // - address relation için ID'yi garanti et
            const fallbackCompanyName =
                payload.company_name?.trim() ||
                `${payload.name || ''} ${payload.last_name || ''}`.trim() ||
                payload.name?.trim() ||
                'Yeni Alt Acente';

            const submitPayload = {
                address: payload.address ? parseInt(payload.address, 10) : payload.address,
                name: payload.name?.trim() || '',
                last_name: payload.last_name?.trim() || '',
                phone: payload.phone?.trim() || '',
                email: payload.email?.trim() || '',
                company_name: fallbackCompanyName,
                company_address: payload.company_address?.trim() || '',
                company_country: payload.company_country?.trim() || '',
                company_city: payload.company_city?.trim() || '',
            };

            await onSave(submitPayload);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4">
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={onClose}></div>
                <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl transform transition-all border border-slate-200 dark:border-slate-800">
                    <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800">
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                            {editingItem ? 'Alt Acenteyi Düzenle' : 'Yeni Alt Acente Ekle'}
                        </h3>
                        <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full text-slate-400 transition-colors">
                            <XMarkIcon className="w-6 h-6" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Ad</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Soyad</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Email</label>
                                <input
                                    type="email"
                                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Telefon</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                />
                            </div>
                            <div className="md:col-span-2 space-y-1.5">
                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Address</label>
                                <select
                                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                                    value={formData.address}
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                >
                                    <option value="">Address seçiniz...</option>
                                    {addresses.map((a) => (
                                        <option key={a.id} value={a.id}>
                                            {`${a.name || ''} ${a.lname || ''}`.trim() || 'İsimsiz Adres'}
                                            {a.city ? ` - ${a.city}` : ''}
                                            {a.country ? `, ${a.country}` : ''}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-slate-400 ml-1">
                                    Boş bırakılırsa Firma Adresi + Ülke + Şehir bilgilerinden otomatik Address oluşturulur.
                                </p>
                            </div>
                            <div className="md:col-span-2 space-y-1.5">
                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Alt Acente / Şube Adı</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                                    value={formData.company_name}
                                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                                />
                            </div>
                            <div className="md:col-span-2 lg:col-span-4 space-y-1.5">
                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Firma Adresi</label>
                                <textarea
                                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white min-h-[80px]"
                                    value={formData.company_address}
                                    onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Ülke</label>
                                <div className="relative">
                                    <CreatableSelect
                                        className="react-select-container text-xs"
                                        classNamePrefix="react-select"
                                        options={countryOptions}
                                        value={countryOptions.find((opt) => opt.value === String(formData.company_country_id)) || null}
                                        onChange={(selectedOption) => {
                                            const selectedId = selectedOption?.value || '';
                                            const selectedCountry = countries.find((c) => String(c.id) === String(selectedId));
                                            setFormData({
                                                ...formData,
                                                company_country_id: selectedId,
                                                company_country: selectedCountry?.country_name || '',
                                                company_city_id: '',
                                                company_city: '',
                                            });
                                        }}
                                        placeholder="Ülke seçiniz..."
                                        isClearable
                                        isSearchable
                                        formatCreateLabel={(inputValue) => `"${inputValue}" arayın...`}
                                        onCreateOption={() => {}}
                                        components={{ DropdownIndicator: null, IndicatorSeparator: null }}
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '42px',
                                            }),
                                            valueContainer: (base) => ({
                                                ...base,
                                                padding: '2px 34px 2px 10px',
                                            }),
                                            menu: (base) => ({
                                                ...base,
                                                zIndex: 10000,
                                            }),
                                            menuList: (base) => ({
                                                ...base,
                                                overflowX: 'hidden',
                                            }),
                                            option: (base) => ({
                                                ...base,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }),
                                        }}
                                        classNames={{
                                            control: (state) =>
                                                `bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white ${state.isFocused ? 'ring-2 ring-blue-500 border-blue-500' : ''}`,
                                            singleValue: () => 'text-slate-900 dark:text-white',
                                            input: () => 'text-slate-900 dark:text-white',
                                            placeholder: () => 'text-slate-400 dark:text-slate-500',
                                            menu: () => 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl mt-1 shadow-lg',
                                            menuList: () => 'p-1',
                                            option: (state) =>
                                                `cursor-pointer px-3 py-2 rounded-md ${state.isFocused ? 'bg-blue-50 dark:bg-slate-800' : ''} ${state.isSelected ? 'bg-blue-600 text-white' : 'text-slate-900 dark:text-slate-100'}`
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={openCountryModal}
                                        className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10 h-6 w-6 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
                                        title="Yeni ülke ekle"
                                    >
                                        <PlusIcon className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Şehir</label>
                                <div className="relative">
                                    <CreatableSelect
                                        className="react-select-container text-xs"
                                        classNamePrefix="react-select"
                                        options={cityOptions}
                                        value={cityOptions.find((opt) => opt.value === String(formData.company_city_id)) || null}
                                        onChange={(selectedOption) => {
                                            const selectedId = selectedOption?.value || '';
                                            const selectedCity = filteredCities.find((c) => String(c.id) === String(selectedId));
                                            setFormData({
                                                ...formData,
                                                company_city_id: selectedId,
                                                company_city: selectedCity?.city_name || '',
                                            });
                                        }}
                                        placeholder="Şehir seçiniz..."
                                        isClearable
                                        isSearchable
                                        formatCreateLabel={(inputValue) => `"${inputValue}" arayın...`}
                                        onCreateOption={() => {}}
                                        components={{ DropdownIndicator: null, IndicatorSeparator: null }}
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '42px',
                                            }),
                                            valueContainer: (base) => ({
                                                ...base,
                                                padding: '2px 34px 2px 10px',
                                            }),
                                            menu: (base) => ({
                                                ...base,
                                                zIndex: 10000,
                                            }),
                                            menuList: (base) => ({
                                                ...base,
                                                overflowX: 'hidden',
                                            }),
                                            option: (base) => ({
                                                ...base,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }),
                                        }}
                                        classNames={{
                                            control: (state) =>
                                                `bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white ${state.isFocused ? 'ring-2 ring-blue-500 border-blue-500' : ''}`,
                                            singleValue: () => 'text-slate-900 dark:text-white',
                                            input: () => 'text-slate-900 dark:text-white',
                                            placeholder: () => 'text-slate-400 dark:text-slate-500',
                                            menu: () => 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl mt-1 shadow-lg',
                                            menuList: () => 'p-1',
                                            option: (state) =>
                                                `cursor-pointer px-3 py-2 rounded-md ${state.isFocused ? 'bg-blue-50 dark:bg-slate-800' : ''} ${state.isSelected ? 'bg-blue-600 text-white' : 'text-slate-900 dark:text-slate-100'}`
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={openCityModal}
                                        className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10 h-6 w-6 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
                                        title="Yeni şehir ekle"
                                    >
                                        <PlusIcon className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {editingItem && (
                            <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 p-4">
                                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                                    <PhoneIcon className="w-4 h-4" />
                                    Bu acente ile görüşmeler
                                </h4>
                                {loadingSalesCalls ? (
                                    <div className="flex items-center justify-center py-8">
                                        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                ) : salesCalls.length === 0 ? (
                                    <p className="text-xs text-slate-500 dark:text-slate-400 py-4 text-center">
                                        Bu acente ile kayıtlı görüşme bulunamadı.
                                    </p>
                                ) : (
                                    <div className="max-h-56 overflow-y-auto overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
                                        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700 text-xs">
                                            <thead className="bg-slate-50 dark:bg-slate-800/60">
                                                <tr>
                                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Type</th>
                                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Contact</th>
                                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Country/City</th>
                                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Rep.</th>
                                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Start</th>
                                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">End</th>
                                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Status</th>
                                                    <th className="px-3 py-2 text-left font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Result</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white dark:bg-slate-800/30 divide-y divide-slate-100 dark:divide-slate-700">
                                                {salesCalls.map((call) => {
                                                    const formattedCall = {
                                                        ...call,
                                                        start_date: call.start_date ? call.start_date.replace('T', ' ').substring(0, 16) : '',
                                                        end_date: call.end_date ? call.end_date.replace('T', ' ').substring(0, 16) : '',
                                                    };
                                                    return (
                                                        <tr
                                                            key={call.id}
                                                            onClick={() => onOpenSalesCall?.(formattedCall)}
                                                            className="cursor-pointer hover:bg-blue-50/50 dark:hover:bg-slate-700/50 transition-colors"
                                                        >
                                                            <td className="px-3 py-2 whitespace-nowrap text-slate-700 dark:text-slate-300">{call.type || '-'}</td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-400">{call.agency_contact || '-'}</td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-400">
                                                                {[call.country, call.city].filter(Boolean).join(' / ') || '-'}
                                                            </td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-400">{call.sales_representative || '-'}</td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-400">
                                                                {call.start_date ? new Date(call.start_date).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                                                            </td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-400">
                                                                {call.end_date ? new Date(call.end_date).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                                                            </td>
                                                            <td className="px-3 py-2 whitespace-nowrap">
                                                                <span
                                                                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                                                        call.call_status === 'Tamamlandı'
                                                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                                                            : call.call_status === 'Planlandı'
                                                                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                                                                : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                                                                    }`}
                                                                >
                                                                    {getSalesCallStatusLabel(call.call_status) || '-'}
                                                                </span>
                                                            </td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-slate-600 dark:text-slate-400">{getSalesCallStatusLabel(call.call_result) || '-'}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mt-8 flex items-center justify-end gap-3">
                            <Button variant="outline" type="button" onClick={onClose}>İptal</Button>
                            <Button type="submit" disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg">
                                {editingItem ? 'Güncelle' : 'Kaydet'}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>

            {isCountryModalOpen && (
                <div className="fixed inset-0 z-[10010] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/50" onClick={() => setIsCountryModalOpen(false)}></div>
                    <div className="relative w-full max-w-md rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-5 shadow-2xl">
                        <h4 className="text-base font-bold text-slate-900 dark:text-white mb-4">Yeni Ülke Ekle</h4>
                        <form onSubmit={handleCreateCountry} className="space-y-4">
                            <input
                                type="text"
                                value={newCountryName}
                                onChange={(e) => setNewCountryName(e.target.value)}
                                placeholder="Ülke adı"
                                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                                autoFocus
                            />
                            <div className="flex items-center justify-end gap-2">
                                <Button type="button" variant="outline" onClick={() => setIsCountryModalOpen(false)}>İptal</Button>
                                <Button type="submit" disabled={isCreatingCountry}>{isCreatingCountry ? 'Kaydediliyor...' : 'Kaydet'}</Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isCityModalOpen && (
                <div className="fixed inset-0 z-[10010] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/50" onClick={() => setIsCityModalOpen(false)}></div>
                    <div className="relative w-full max-w-md rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-5 shadow-2xl">
                        <h4 className="text-base font-bold text-slate-900 dark:text-white mb-4">Yeni Şehir Ekle</h4>
                        <form onSubmit={handleCreateCity} className="space-y-4">
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                                Seçili ülke: {countries.find((c) => String(c.id) === String(formData.company_country_id))?.country_name || '-'}
                            </div>
                            <input
                                type="text"
                                value={newCityName}
                                onChange={(e) => setNewCityName(e.target.value)}
                                placeholder="Şehir adı"
                                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                                autoFocus
                            />
                            <div className="flex items-center justify-end gap-2">
                                <Button type="button" variant="outline" onClick={() => setIsCityModalOpen(false)}>İptal</Button>
                                <Button type="submit" disabled={isCreatingCity}>{isCreatingCity ? 'Kaydediliyor...' : 'Kaydet'}</Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>,
        document.body
    );
};

export default SubAgencyFormModal;
